import type { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs';

import { ContentApi } from '@apps/main/modules/api/content/contentApi';

import type {
  HotelConstraintQL,
  DetailsStaticInfo,
  Coordinate,
  IngredientId,
  DestinationInformation,
} from '@libs/trip-planner-interfaces';
import { StaticInfoType } from '@libs/trip-planner-interfaces';
import { FullTextConstraint } from '@libs/constraint-builder';

import { searchHotels } from '../helpers/search';
import { createStaticInfo } from '../helpers/createStaticInfo';

export class DetailsStaticInfoSearchService {
  private contentApi = new ContentApi();

  public search(
    hotelCode: string,
    ingredientId: IngredientId
  ): Observable<DetailsStaticInfo> {
    const constraint: HotelConstraintQL = {
      hotelCodes: [hotelCode],
      maxNumberOfHotels: 1,
      selectedHotelTypes: [],
      boardBasis: [],
    };

    return searchHotels(constraint, StaticInfoType.Details).pipe(
      mergeMap(({ response: getHotelsResponse, type }) =>
        getHotelsResponse.hotels.map((hotel) =>
          createStaticInfo(hotel, getHotelsResponse, ingredientId, type)
        )
      )
    );
  }

  public searchHotelName(inputText: string): Observable<string> {
    return this.contentApi
      .getHotels(
        { selection: new FullTextConstraint(inputText), maxNumberOfHotels: 1 },
        [],
        StaticInfoType.NameOnly
      )
      .pipe(map(({ response }) => response.hotels[0]?.name ?? inputText));
  }

  public searchHotelCode(hotelName: string): Observable<string | undefined> {
    return this.contentApi
      .getHotels(
        { selection: new FullTextConstraint(hotelName), maxNumberOfHotels: 1 },
        [],
        StaticInfoType.NameOnly
      )
      .pipe(
        map(({ response }) => {
          if (
            response.hotels[0] &&
            hotelName.includes(response.hotels[0].name)
          ) {
            return response.hotels[0]?.hotelCode;
          }
        })
      );
  }

  public searchHotelMainPolygon(
    inputText: string
  ): Observable<{ name?: string; id?: number }> {
    return this.contentApi
      .getHotels(
        { selection: new FullTextConstraint(inputText), maxNumberOfHotels: 1 },
        [],
        StaticInfoType.MainPolygonOnly
      )
      .pipe(
        map(({ response }) => ({
          name: response.hotels[0]?.mainPolygon?.fullName,
          id: response.hotels[0]?.mainPolygon?.id,
        }))
      );
  }

  public searchHotelPosition(
    inputText: string
  ): Observable<Coordinate | undefined> {
    return this.contentApi
      .getHotels(
        { selection: new FullTextConstraint(inputText), maxNumberOfHotels: 1 },
        [],
        StaticInfoType.PositionOnly
      )
      .pipe(
        map(({ response }) => {
          const firstResult = response.hotels[0];

          if (firstResult) {
            return {
              latitude: firstResult.position.latitude.decimalDegrees,
              longitude: firstResult.position.longitude.decimalDegrees,
            };
          }
        })
      );
  }

  public searchDestinationInformation(
    polygonId: number
  ): Observable<DestinationInformation> {
    return this.contentApi.getDestinationInformation(polygonId).pipe(
      map((destinationInformation) => ({
        city: destinationInformation.placeCityName,
        destination: destinationInformation.polygonName,
        description: destinationInformation.baseMarkup,
        image: destinationInformation.imageNames
          ? destinationInformation.imageNames[0]
          : undefined,
      }))
    );
  }
}
